import React, { useState } from 'react';
import { AttentionItem } from '../../domain/types';
import { useZunelo } from '../../context/ZuneloContext';
import { MetricsService } from '../../services/metricsService';
import { FlowService } from '../../services/flowService';
import { JiraCardExpanded } from './JiraCardExpanded';
import { JiraCardFooter } from './JiraCardFooter';
import { JiraCardHeader } from './JiraCardHeader';
import { JiraDetailModal } from './JiraDetailModal';
import { useDetailSurfaceParam } from '../detail/useDetailSurfaceParam';
import {
  JIRA_STANDARD_STATUSES,
  jiraAccentStripBackground,
  jiraArticleBackground,
  jiraArticleShadow,
} from './jiraCardVisuals';
import { projectsForJiraCard } from '../../domain/projectCardIdentity';
import { useWorkContextMembership } from '../../hooks/useWorkContextMembership';
import { useRightWorkspaceSidebar } from '../layout/RightWorkspaceSidebar';

interface JiraCardProps {
  item: AttentionItem;
  isSelected?: boolean;
}

export const JiraCard: React.FC<JiraCardProps> = ({ item: itemProp, isSelected = false }) => {
  const {
    selectItem,
    openContextSurface,
    isItemExpanded,
    toggleItemExpanded,
    updateWorkflowState,
    toggleAcceptanceCriterion,
    addAcceptanceCriterion,
    updateAcceptanceCriterion,
    deleteAcceptanceCriterion,
    cycleSubtaskState,
    generateIssueSummary,
    navigateToItem,
    getJiraItemByKey,
    getGitHubItemsByTicket,
    getItemById,
    getHoldInfo,
    navigateToContext,
  } = useZunelo();

  const item = getItemById(itemProp.id) || itemProp;
  const isExpanded = isItemExpanded(item.id);
  const workContext = useWorkContextMembership(item.id);
  const { restore: restoreRightSidebar } = useRightWorkspaceSidebar();
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useDetailSurfaceParam(item.id);

  const workflow = item.workflow || 'Tareas por hacer';
  const progress = MetricsService.calculateAcceptanceProgress(item.criteria);
  const holdInfo = getHoldInfo(item.ticket);
  const linkedPullRequests = getGitHubItemsByTicket(item.ticket);
  const projectChips = projectsForJiraCard(
    {
      components: item.components || item.rawJiraIssue?.fields.components || [],
    },
    linkedPullRequests,
  );
  const availableTransitions = Array.from(
    new Set([...JIRA_STANDARD_STATUSES, ...FlowService.getAvailableTransitions(workflow)]),
  );

  const handleCardClick = () => {
    selectItem(item.id);
    toggleItemExpanded(item.id);
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleItemExpanded(item.id);
    selectItem(item.id);
  };

  const handleNextFlow = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = FlowService.getNextWorkflowState(workflow);
    if (next && item.ticket) updateWorkflowState(item.ticket, next);
  };

  const navigateToJira = (key: string) => {
    const linked = getJiraItemByKey(key);
    if (linked) navigateToItem('jira', linked.id);
  };

  const navigateToGitHub = (id: string) => {
    navigateToItem('github', id);
  };

  const handleFooterAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectItem(item.id);
    if (!isExpanded) {
      toggleItemExpanded(item.id);
    } else {
      setIsDetailOpen(true);
    }
  };

  const handleGenerateSummary = () => {
    if (!item.ticket || summaryBusy) return;
    setSummaryBusy(true);
    void generateIssueSummary(item.ticket).finally(() => setSummaryBusy(false));
  };

  return (
    <>
      <article
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px 18px',
          borderRadius: 18,
          cursor: 'pointer',
          overflow: 'hidden',
          background: jiraArticleBackground(isSelected),
          border: 'none',
          boxShadow: jiraArticleShadow(isSelected),
          transition: 'all 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: jiraAccentStripBackground(item, workflow),
          }}
        />

        <JiraCardHeader
          item={item}
          workflow={workflow}
          isExpanded={isExpanded}
          projectChips={projectChips}
          workContext={workContext}
          onCardClick={handleCardClick}
          onToggleExpanded={handleToggleExpanded}
          onNavigateToJira={navigateToJira}
          onNavigateToGitHub={navigateToGitHub}
          onNavigateToContext={() => workContext && navigateToContext(workContext.id)}
          onOpenContext={() => {
            openContextSurface(item.id);
            restoreRightSidebar();
          }}
          onOpenDetail={() => setIsDetailOpen(true)}
        />

        {isExpanded ? (
          <JiraCardExpanded
            item={item}
            workflow={workflow}
            availableTransitions={availableTransitions}
            holdInfo={holdInfo}
            linkedPullRequests={linkedPullRequests}
            summaryBusy={summaryBusy}
            onGenerateSummary={handleGenerateSummary}
            onUpdateWorkflow={updateWorkflowState}
            onNextFlow={handleNextFlow}
            onNavigateToJira={navigateToJira}
            isJiraRefResolved={(key) => Boolean(getJiraItemByKey(key))}
            onNavigateToGitHub={navigateToGitHub}
            onToggleCriterion={toggleAcceptanceCriterion}
            onAddCriterion={addAcceptanceCriterion}
            onUpdateCriterion={updateAcceptanceCriterion}
            onDeleteCriterion={deleteAcceptanceCriterion}
            onCycleSubtask={cycleSubtaskState}
            onOpenDetail={() => setIsDetailOpen(true)}
          />
        ) : null}

        <JiraCardFooter
          item={item}
          isSelected={isSelected}
          isExpanded={isExpanded}
          progress={progress}
          onActionClick={handleFooterAction}
        />
      </article>

      {isDetailOpen ? (
        <JiraDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} item={item} />
      ) : null}
    </>
  );
};
